import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";

const API = `${window.location.origin}/api`;

const TOTAL = 1333;
const CAP = 1150;
const FALLBACK = 198;
const CACHE_KEY = "vdi_blanche_alloc_v2";
const TICK_FLAG = "vdi_blanche_ticked_v2";

function cachedValue() {
  try {
    const v = parseInt(localStorage.getItem(CACHE_KEY), 10);
    if (!isNaN(v)) return Math.min(v, CAP);
  } catch {}
  return FALLBACK;
}

export default function BlancheCounter() {
  const [value, setValue] = useState(cachedValue);
  const [flash, setFlash] = useState(false);
  const [inView, setInView] = useState(false);
  const tickedRef = useRef(false);
  const ref = useRef(null);

  const commit = (v) => {
    const clamped = Math.min(v, CAP);
    setValue(clamped);
    try {
      localStorage.setItem(CACHE_KEY, String(clamped));
    } catch {}
  };

  // Load the global counter from the server
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/blanche/allocations`);
        commit(data.value);
      } catch {
        /* keep cached fallback */
      }
    })();
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => setInView(e.isIntersecting),
      { threshold: 0.4 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  // ONE allocation (+6, one case) max per visit, only while reading the section
  useEffect(() => {
    if (!inView || tickedRef.current || value >= CAP) return;
    try {
      if (sessionStorage.getItem(TICK_FLAG)) {
        tickedRef.current = true;
        return;
      }
    } catch {}
    const delay = 9000 + Math.random() * 12000;
    const t = setTimeout(async () => {
      tickedRef.current = true;
      try {
        sessionStorage.setItem(TICK_FLAG, "1");
      } catch {}
      try {
        const { data } = await axios.post(`${API}/blanche/allocations/tick`);
        if (data.value > value) {
          commit(data.value);
          setFlash(true);
          setTimeout(() => setFlash(false), 4000);
        }
      } catch {
        /* server unreachable: no fake local tick */
      }
    }, delay);
    return () => clearTimeout(t);
  }, [inView, value]);

  const pct = Math.min(100, (value / TOTAL) * 100);
  const soldOutSoon = value >= CAP;

  return (
    <div
      ref={ref}
      data-testid="blanche-counter"
      className="w-full border rounded-2xl px-5 sm:px-7 py-4 sm:py-5 grid grid-cols-1 sm:grid-cols-2 items-start gap-5 sm:gap-8"
      style={{ borderColor: "rgba(196,166,113,0.4)", background: "rgba(196,166,113,0.05)" }}
    >
      {/* Sinistra: numeri registro + CTA */}
      <div>
        <p className="overline text-[9px] mb-1.5" style={{ color: "#C4A671" }}>
          Registro Allocazioni — In Tempo Reale
        </p>
        <div className="flex items-baseline gap-2 flex-wrap">
          <AnimatePresence mode="popLayout">
            <motion.span
              key={value}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
              className="font-serif-display text-3xl sm:text-4xl leading-none"
              style={{ color: flash ? "#E0C48A" : "#C4A671" }}
              data-testid="blanche-counter-value"
            >
              {value}
            </motion.span>
          </AnimatePresence>
          <span className="font-serif-display italic text-base text-[#F2EFE8]">
            / {TOTAL.toLocaleString("it-IT")} allocate
          </span>
          <span className="overline text-[9px]" style={{ color: "#9a9a9a" }}>
            · {(TOTAL - value).toLocaleString("it-IT")} disponibili
          </span>
        </div>
        <div className="mt-2.5 h-[3px] w-full max-w-[300px]" style={{ background: "#1f1f1f" }}>
          <motion.div
            className="h-full"
            style={{ background: "linear-gradient(90deg, #8B7A55, #C4A671)" }}
            initial={{ width: 0 }}
            whileInView={{ width: `${pct}%` }}
            viewport={{ once: true }}
            transition={{ duration: 1.4, ease: "easeOut" }}
          />
        </div>
        <AnimatePresence>
          {flash && (
            <motion.span
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0 }}
              className="overline text-[9px] mt-1.5 inline-block"
              style={{ color: "#E0C48A" }}
              data-testid="blanche-counter-tick"
            >
              ✦ una nuova allocazione è appena partita
            </motion.span>
          )}
        </AnimatePresence>
        {soldOutSoon && (
          <p
            className="mt-1.5 overline text-[9px]"
            style={{ color: "#E0C48A" }}
            data-testid="blanche-counter-soldout"
          >
            ✦ Lotto quasi esaurito — ultime allocazioni via Concierge
          </p>
        )}

        {/* CTA sotto il registro */}
        <button
          onClick={() =>
            document.getElementById("contatti")?.scrollIntoView({ behavior: "smooth" })
          }
          className="mt-4 inline-flex items-center gap-3 px-6 py-3 border border-[#C4A671] text-[#C4A671] transition-colors hover:bg-[#C4A671] hover:text-[#0A0A0A] whitespace-nowrap"
          style={{
            fontFamily: "'Outfit', sans-serif",
            fontSize: "10px",
            letterSpacing: "0.24em",
            textTransform: "uppercase",
          }}
          data-testid="blanche-cta"
        >
          Richiedi Allocazione Privata <span aria-hidden>→</span>
        </button>
      </div>

      {/* Destra: nota del caveau */}
      <p
        className="text-[12px] leading-[1.6] font-light border-t pt-3 sm:border-t-0 sm:pt-0 sm:border-l sm:pl-7"
        style={{ color: "#C9C3B5", borderColor: "#2a2a2a" }}
        data-testid="blanche-counter-note"
      >
        <em className="font-serif-display not-italic text-[#F2EFE8]">Nota del caveau</em> — le
        numerazioni non seguono l&apos;ordine di allocazione: ogni cassa riceve numeri
        estratti casualmente dal registro (es. #0058, #0236, #1023). Nessuna sequenza,
        nessun privilegio: solo il destino della bottiglia che arriva nelle vostre mani.
      </p>
    </div>
  );
}
